import { useState } from 'react';
import { api } from '@/services/api';
import { useAuth, type VvfUser } from '@/shared/AuthContext';

/**
 * Gate "cambio password obbligatorio" (F19): mostrato al posto dell'app quando
 * l'utente ha mustChangePassword=true (password temporanea assegnata
 * dall'import da Firebase). L'utente NON puo' navigare altrove finche' non
 * imposta una nuova password: il gate vive in DashboardLayout, prima
 * dell'Outlet, quindi copre tutte le route protette.
 *
 * Riusa l'endpoint cambio password esistente (PUT /users/me/settings, M2:
 * richiede la password corrente — cioe' quella temporanea appena usata per
 * il login). Al successo il backend azzera il flag e ritorna l'utente
 * aggiornato: setUser() lo propaga e il gate si toglie da solo.
 */
export function ChangePasswordGate(): React.JSX.Element {
  const { user, setUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('La nuova password deve essere di almeno 8 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    if (newPassword === currentPassword) {
      setError('La nuova password deve essere diversa da quella temporanea');
      return;
    }

    setLoading(true);
    try {
      // Nickname/bypassVolume invariati: il PUT settings li richiede sempre.
      const res = await api.put('/users/me/settings', {
        nickname: user?.nickname ?? '',
        bypassVolume: user?.bypassVolume === true,
        newPassword,
        currentPassword,
      });
      // L'utente aggiornato ha mustChangePassword=false → il gate sparisce.
      setUser(res.data as VvfUser);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Errore durante il cambio password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Cambio password obbligatorio</p>
        </div>

        <div className="login-form-wrap">
          <h2>🔑 Imposta una nuova password</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>
            Stai usando una <strong>password temporanea</strong>. Per motivi di
            sicurezza devi sceglierne una nuova prima di continuare.
          </p>
          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="currentPassword">Password temporanea (attuale)</label>
              <input id="currentPassword" type="password" value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)} autoFocus
                autoComplete="current-password" />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nuova password (min. 8 caratteri)</label>
              <input id="newPassword" type="password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password" />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Conferma nuova password</label>
              <input id="confirmPassword" type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password" />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Salvataggio...' : '💾 Salva nuova password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
